import { getDefaultDateRange } from './date-utils';
import { LineChart, SensorStatusPieChart, AlertHistoryLineChart } from './charting';
import { getTemperatureReadingsByDevice, getTemperatureReadingsBySensorGroup, setDashboardChartOrder, Location } from './requests';
import { UrlHelper, UrlHelperConfiguration } from './url-helper';
import { LineChartToolbar } from './line-chart-toolbar';
// @ts-ignore
import dragula from 'dragula';

interface LocationDashboardConfiguration {
    ids: LocationDashboardPageElementIds;
    pageConfiguration: LocationDashboardPageConfiguration;
    urlHelperConfiguration: UrlHelperConfiguration;
}

interface LocationDashboardPageElementIds {
    lineChartPrefix: string;
    locationSelect: string;
    refreshButton: string;
    sensorStatusPieChart: string;
    alertHistoryLineChart: string;
    chartContainer: string;
}

interface LocationDashboardPageConfiguration {
    date: string;
    statistics: LocationDashboardStatistics;
    currentLocation: Location;
    charts: ChartConfig[];
    timeZoneId: string;
}

interface LocationDashboardStatistics {
    totalSensors: number;
    offlineSensors: number;
    alertingSensors: number;
    alertsToday: number;
    alerts7days: number;
    alerts30days: number;
    alertHistogram: AlertHistogramEntry[];
}

interface ChartConfig {
    id: number;
    type: "Device" | "SensorGroup";
    deviceId?: number;
    sensorGroupId?: number;
}

interface AlertHistogramEntry {
    x: string;
    y: number;
}

$(document).on("turbolinks:load", function() {
    const configElement = document.getElementById("device-dashboard-config");
    if (configElement) {
        const config: LocationDashboardConfiguration = JSON.parse(configElement.innerHTML);
        const page = new LocationDashboardPage(config.ids, config.pageConfiguration, config.urlHelperConfiguration);
        $(document).one("turbolinks:before-render", () => page.destroy());
    }
});

declare var Turbolinks: any;

export class LocationDashboardPage {
    private charts: LineChart[] = [];
    private chartControls: LineChartToolbar[] = [];
    private sensorStatusPieChart: SensorStatusPieChart | null = null;
    private alertHistoryLineChart: AlertHistoryLineChart | null = null;
    private urlHelper: UrlHelper;

    constructor(private elementIds: LocationDashboardPageElementIds, private pageConfig: LocationDashboardPageConfiguration, private urlHelperConfiguration: UrlHelperConfiguration) {
        this.urlHelper = new UrlHelper(urlHelperConfiguration);
        $(`#${elementIds.refreshButton}`).on("click", () => {
            Turbolinks.clearCache();
            Turbolinks.visit(window.location);
        });

        const $locationSelect = $(`#${elementIds.locationSelect}`);
        $locationSelect.on("change", () => {
            this.urlHelper.navigate(this.urlHelper.formatDashboardUrl($locationSelect.val() as any));
        });

        this.renderAllSensorReadingCharts();
        this.sensorStatusPieChart = new SensorStatusPieChart(
            this.pageConfig.statistics.totalSensors,
            this.pageConfig.statistics.offlineSensors,
            this.pageConfig.statistics.alertingSensors,
            this.elementIds.sensorStatusPieChart,
            this.pageConfig.currentLocation.id,
            this.urlHelper);
        this.sensorStatusPieChart.render();

        this.alertHistoryLineChart = new AlertHistoryLineChart(this.pageConfig.statistics.alertHistogram, this.elementIds.alertHistoryLineChart);
        this.alertHistoryLineChart.render();

        const dragulaHandle = $(`#${this.elementIds.chartContainer}`).get(0);

        dragula([dragulaHandle], {
            removeOnSpill: false
        }).on("dragend", () => {
            const charts: { sensorGroupId?: number, deviceId?: number }[] = [];
            dragulaHandle.querySelectorAll('div[data-chart-id]').forEach(chartElement => {
                const id = parseInt(chartElement.getAttribute('data-chart-id'));
                const chart = this.pageConfig.charts.find(c => c.id === id);
                charts.push({ deviceId: chart.deviceId, sensorGroupId: chart.sensorGroupId });
            });
            setDashboardChartOrder(this.pageConfig.currentLocation, charts);
        });
    }

    public destroy() {
        this.charts.forEach(c => c.destroy());
        this.charts = [];
        this.sensorStatusPieChart && this.sensorStatusPieChart.destroy();
        this.sensorStatusPieChart = null;
        this.alertHistoryLineChart && this.alertHistoryLineChart.destroy();
        this.alertHistoryLineChart = null;
        this.chartControls.forEach(c => c.destroy());
        this.chartControls = [];
    }

    private renderAllSensorReadingCharts() : void {
        this.charts.forEach(c => c.destroy());
        this.charts = [];

        for (let graphCounter = 0; graphCounter < this.pageConfig.charts.length; graphCounter++) {
            this.renderSensorReadingChart(`${this.elementIds.lineChartPrefix}-${graphCounter.toString()}`, this.pageConfig.charts[graphCounter]);
        }
    }

    private async renderSensorReadingChart(chartElementId: string, chartConfig: ChartConfig): Promise<void> {
        const [defaultFromDate, defaultToDate] = getDefaultDateRange(this.pageConfig.date && new Date(this.pageConfig.date));
        const chart = new LineChart(
            chartElementId,
            "dashboard",
            defaultFromDate,
            defaultToDate,
            (fromDate, toDate) => {
                switch (chartConfig.type) {
                    case "Device":
                        return getTemperatureReadingsByDevice(chartConfig.deviceId, fromDate, toDate);
                    case "SensorGroup":
                        return getTemperatureReadingsBySensorGroup(fromDate, toDate, chartConfig.sensorGroupId);
                }
            },
            this.urlHelper,
            this.pageConfig.timeZoneId);

        chart.firstRender();
        this.charts.push(chart);
        this.chartControls.push(new LineChartToolbar($(`div[data-chart-id='${chartConfig.id}']`), chart, "up"));
    }
}
